// What the service recorded against this activation, newest first.
//
// ---------------------------------------------------------------------------
// THE THREE SENTENCES ARE THE SCREEN AND THE TABLE IS THE EVIDENCE
//
// This is Unverified.tsx's layout ruling arriving at a different read and
// landing the same way. There, the tally is the answer and the addresses are
// what it was computed from. Here, `subset_note` says what this feed is and is
// not, `attribution_note` says why a person is named on some rows and not
// others, and `retention.note` says that nothing deletes any of it. A reader who
// has those three has the finding. The table under them is the working, and it
// is a table nobody can act on without the first two: a column of eighteen
// phrases about tablets and shared tokens reads as a system that has lost track
// of who did things, until the sentence above it says that a capture row records
// a tablet because nobody presses a button to make a registration complete.
//
// So every one of them is on the screen before the rows, they are the service's
// words rather than this screen's, and the api module refuses a response that
// arrives without them.
//
// ---------------------------------------------------------------------------
// THE THREE ATTRIBUTION ARMS DRAW AS THREE DIFFERENT THINGS, WHICH IS THE ONE
// THING ON THIS SCREEN THAT WOULD BE WRONG AND QUIET
//
// A blank cell is the failure this feed was shaped to avoid, and 131 names the
// exact reading: a client viewer, shown nothing in the actor column of the row
// recording their own export, concludes that nobody ran it. The three arms are
// "not shown to you", "there was never a person" and "here is who", and drawn
// identically they are indistinguishable on a screen that looks completely
// fine. There is no visual default that is safe, so each arm has its own mark
// and the sentence explaining all three is the service's, once, under the table.
//
// ---------------------------------------------------------------------------
// A CLIENT VIEWER GETS THIS SCREEN, AND NOTHING HERE TESTS A ROLE
//
// Unverified.tsx refuses one locally, at length and with three reasons. None of
// them is true here, and the difference is the point of migration 131: it
// grants `activity_feed` to both reporting roles and puts the four columns 129
// withholds in a second view, so a client viewer's page is narrower rather than
// refused. This is App.tsx's standing rule with no exception attached, and the
// rail advertises the screen to both roles for the same reason.
//
// `role` is taken and passed to `Refusal` only, which needs it to decide
// whether an enrolment offer belongs under a failure. It decides nothing about
// what is drawn.
//
// ---------------------------------------------------------------------------
// WHAT THE DESIGN ASKS FOR THAT IS NOT HERE, AND WHY, IN ONE PLACE
//
// The View button on every row. `cannot_answer`'s last entry refuses the page
// behind it: only the spine carries a payload, most of a payload is what a
// person typed at a stand, and this is the one screen in the console a client
// viewer reads on every row. Nothing was built and nothing is stubbed, because a
// disabled button is a promise with a date on it.
//
// The actor filter. The design's matches an email address, and the entry above
// that one says there is no address here to match. What could honestly be
// narrowed on is the kind of thing that acted, and the read takes `action` and
// `entity` and nothing else, so there is no way to ask for it: a control here
// would filter the fifty rows this browser holds while looking like it filtered
// the feed, which is the confusion `oneOf` refuses a 400 to prevent one
// parameter over. It is reported rather than approximated.
//
// The entity box, which the design makes free text over a name or an id. The
// service's `entity` is one of eight words and there is no filter on
// `entity_id`, so it is a select with the service's own vocabulary in it. A text
// box would be a field where three quarters of what somebody typed came back a
// 400.
//
// The page count. There is no total and there must not be one: the feed is
// appended to while somebody reads it, so both `offset` and `count(*)` are
// wrong in ways with no error attached. `more` is what replaces them and it is
// all the pager below says.
//
// The retention footer, which claims the log goes with the activation. Nothing
// deletes any of the three sources. The service sends the agreed period, the
// seal, and one sentence saying plainly that nothing enforces either, and that
// sentence is drawn instead of the design's.

import { DefList, NoticeBox, PageHead } from "@nova/desk-ui/parts";
import { Empty, SecLabel } from "@nova/desk-ui/kit";
import { DeskPage, SortHead, useListSort, type ListSort } from "@nova/desk-ui/page";
import { ListState, Loading } from "@nova/desk-ui/states";
import { useCallback, useEffect, useState, type ReactNode } from "react";
// THE VOCABULARIES ARE THE SERVICE'S OWN CONSTANTS, WHICH IS THE WHOLE REASON
// THIS IMPORT REACHES ACROSS A PACKAGE. `ACTIVITY_ACTIONS` is checked against
// the database view's own `case` by xp/api/check.mjs, so the list in the select
// below is the list the feed can emit, and a value in it can never be one the
// service answers 400 to. A hand-written copy here would be a third list
// nothing watches, drifting the day a fourteenth action is recorded and showing
// up as an option that returns an empty page.
//
// A value import from xp/operator into this bundle, which Export.tsx already
// does for EXPORT_PURPOSE_IDS. Nothing on the path from here to xp/api/src/db.ts
// reaches for a driver: that file is an interface and two helpers with no
// imports at all, so what lands in the browser is the arrays and the tree
// shaker's opinion of the rest.
import { ACTIVITY_ACTIONS, ACTIVITY_ENTITIES, type ActivitySortKey } from "../../../operator/src/activity.js";
import {
  ACTIVITY_PER_PAGE,
  readActivity,
  type ActivityAttribution,
  type ActivityFeedPage,
  type ActivityGap,
  type ActivityLine,
  type ActivityRetention,
} from "../api/activity.js";
import type { ApiOptions, Failure } from "../api/client.js";
import type { SessionToken } from "../session/store.js";
import { ReadFailed } from "./ReadFailed.js";
import { useShow } from "./Show.js";

export type ActivityProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

/**
 * What this screen is, before anything is read.
 *
 * THE LAST SENTENCE IS NOT THE DESIGN'S, WHICH SAYS "reading this page does
 * too" ABOUT WRITING A ROW HERE. Both halves of that are half true and the
 * combination is wrong. Reading this page does file a `report_access` row, so
 * the read is recorded. That row is not on this page: 131's third arm is
 * `where a.report = 'export'`, so the feed carries the files that were produced
 * and no other read of anything. `subset_note` says the same thing in the
 * service's words two paragraphs down, and this says the part a reader would
 * otherwise have to infer from it.
 */
/**
 * The feed's order before anybody presses a head, which is the service's own:
 * newest first. Named so the heads can tell a screen reader so.
 */
const FEED_REST: ListSort<ActivitySortKey> = { key: "when", dir: "desc" };

/** When starts newest first; the words start A to Z. */
const FEED_FIRST: Readonly<Partial<Record<ActivitySortKey, "asc" | "desc">>> = { when: "desc" };

type Paged =
  | { readonly status: "reading" }
  | { readonly status: "read" }
  | { readonly status: "failed"; readonly failure: Failure };

export function Activity({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
}: ActivityProps): ReactNode {
  const show = useShow();
  const [paged, setPaged] = useState<Paged>({ status: "reading" });
  // Bumped by "Ask again"; the read below runs again whenever it changes.
  const [attempt, setAttempt] = useState(0);
  /**
   * The last page the service handed over.
   *
   * KEPT ACROSS A PAGE TURN, WHICH IS WHAT KEEPS THE THREE SENTENCES ON THE
   * SCREEN. People.tsx and Unverified.tsx both carry this argument and it binds
   * hardest here, because of how many of them there are: a frame that unmounted
   * while the next page was fetched would take `subset_note`,
   * `attribution_note`, the retention sentence and seven `cannot_answer`
   * entries off the screen at the moment a fresh page of rows arrived under
   * where they had been.
   */
  const [held, setHeld] = useState<ActivityFeedPage | null>(null);
  /**
   * Where the reader has walked to, as the cursors it took to get there.
   *
   * A STACK RATHER THAN A PAGE NUMBER, BECAUSE A KEYSET HAS NO PAGE NUMBERS.
   * The service sends `next` and nothing else: there is no `previous`, and there
   * could not be one without a second query in the other direction on every
   * read. So going back is the one part of this pager the browser has to hold,
   * and what it holds is where this reader has been, which is the one thing the
   * service cannot know.
   *
   * NOT DRAWN AS A COUNT. `trail.length + 1` is sitting right here and would
   * make a plausible "page 3", and it would be a page number over a list that
   * grows at the end somebody started from: two readers who took the same three
   * steps an hour apart are not on the same rows. The pager says how many rows
   * this page holds and whether there are more, which is what the service
   * actually told it.
   */
  const [trail, setTrail] = useState<readonly string[]>([]);
  /**
   * What the reader asked the service to narrow to, or null for everything.
   *
   * BOTH SENT TO THE SERVICE RATHER THAN APPLIED HERE, and api/activity.ts
   * carries the argument: the vocabulary is closed, the service refuses a value
   * outside it with a 400 that names the vocabulary, and a filter applied in a
   * browser would answer over the fifty rows it happens to hold. Which is why
   * changing either of these is a new read rather than a redraw.
   */
  const [action, setAction] = useState<string | null>(null);
  const [entity, setEntity] = useState<string | null>(null);
  // THE ORDER IS THE SERVICE'S, ASKED FOR AND NOT APPLIED HERE (12 Oct
  // hand-off: every column sorts, on the server). A keyset feed sorted in a
  // browser would be fifty rows in order and the next fifty in a different
  // one; the service orders the whole feed and its cursor walks that order.
  const order = useListSort<ActivitySortKey>(FEED_REST, FEED_FIRST);
  const chosen = order.lit ? order.sort : null;
  const chosenKey = chosen?.key ?? null;
  const chosenDir = chosen?.dir ?? null;
  const { onSort: pressHead } = order;
  // BACK TO THE TOP OF THE NEW ORDER, for the reason the filters below give: a
  // cursor is a position in one ordering, and the service refuses one carried
  // into another rather than starting a page part way down an answer nobody
  // saw the top of.
  const sortBy = useCallback(
    (key: ActivitySortKey) => {
      pressHead(key);
      setTrail(() => []);
    },
    [pressHead],
  );

  const cursor = trail.at(-1) ?? null;

  useEffect(() => {
    // The frame's directory read goes first, which is People's rule and
    // Ride.tsx's before it: two reads started in one tick arrive in whatever
    // order the network settles them, and the heading on this page comes from
    // that one.
    if (show.status !== "ready") return;

    let cancelled = false;
    setPaged({ status: "reading" });

    void (async () => {
      // THE SIZE IS PASSED RATHER THAN LEFT TO THE DEFAULT, though they are the
      // same number. api/activity.ts argues that the page is the unit
      // `report_access` counts a disclosure in, so how big this read was is a
      // fact about this screen and belongs where the read is made. There is no
      // control for it: a keyset page has no pager to hang one on, and a size
      // somebody could change mid-walk would file a different figure against
      // every page of one sitting.
      const result = await readActivity(
        api,
        token,
        activationId,
        { cursor, action, entity },
        ACTIVITY_PER_PAGE,
        chosenKey === null || chosenDir === null ? null : { key: chosenKey, dir: chosenDir },
      );
      if (cancelled) return;
      if (!result.ok) {
        setPaged({ status: "failed", failure: result });
        // A 401 is the one refusal that ends a session. A 403 does not, and
        // signing somebody out for asking to read something they may not read
        // is the mistake client.ts names.
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        return;
      }
      setHeld(result.value);
      setPaged({ status: "read" });
    })();

    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, show.status, cursor, action, entity, attempt, chosenKey, chosenDir]);

  // The filters, drawn while the first page is read as well as after, as the
  // design draws them: a panel that appears only once there are rows moves the
  // page under the reader.
  const narrow = (
    <Narrow
      action={action}
      entity={entity}
      onAction={(next) => {
        setAction(next);
        // BACK TO THE NEWEST ROW, AND THAT IS NOT A CONVENIENCE. A cursor is a
        // position in one ordering of one filtered list. Carried into a
        // different list it still parses, still passes the keyset comparison
        // and still returns rows, so what a reader would get is a page
        // starting part way down an answer they never saw the top of, with
        // nothing anywhere saying so.
        setTrail([]);
      }}
      onEntity={(next) => {
        setEntity(next);
        setTrail([]);
      }}
    />
  );

  // THE 12 OCTOBER PAGE SHAPE. Filter activity is the right-hand column, as
  // console-activity.jsx draws it (`section.side`); the feed beside it; the
  // pager in its slot; Retention and Not available at the foot.
  const head = <PageHead title="Event activity" uuid={activationId} />;

  // A FAILED READ IS DRAWN IN THE PAGE, the design's way (console-activity.jsx,
  // ListState; desks.mdc): the head, Filter activity and the rail stay, and
  // the kit's Failed with Ask again stands where the feed would be.
  const failed =
    show.status === "failed"
      ? {
          failure: show.failure,
          retry: show.reload,
          notCounted: "No activity is shown because the activation did not load.",
        }
      : paged.status === "failed"
        ? {
            failure: paged.failure,
            retry: () => setAttempt((n) => n + 1),
            notCounted: "This page did not load. This does not mean nothing happened.",
          }
        : null;

  if (failed !== null) {
    return (
      <DeskPage head={head} side={narrow}>
        <section>
          <SecLabel>What happened here</SecLabel>
          <ReadFailed
            what="Could not load activity"
            failure={failed.failure}
            role={role}
            onEnrol={onEnrol}
            notCounted={failed.notCounted}
            onRetry={failed.retry}
          />
        </section>
      </DeskPage>
    );
  }

  if (held === null) {
    return (
      <DeskPage head={head} side={narrow}>
        <section>
          <SecLabel>What happened here</SecLabel>
          {/* Five columns because that is what the table below has. A skeleton
              with the wrong number of them is the jump it exists to prevent. */}
          <Loading rows={6} cols={5} label="Loading activity…" />
        </section>
      </DeskPage>
    );
  }

  // THE SERVICE'S ECHO RATHER THAN THIS SCREEN'S STATE, which is the one place
  // the two could disagree and the reason `filters` is on the response at all. A
  // filter this console asked for and the service did not apply is a page whose
  // heading would otherwise lie about which rows are on it, and the honest
  // reading of a mismatch is the service's: it is what produced the rows.
  const narrowed = held.filters.action !== null || held.filters.entity !== null;
  // In the order the service sent them, which is the order asked for.
  const events = held.events;
  const heads = { sort: order.sort, lit: order.lit, onSort: sortBy };

  return (
    <DeskPage
      head={head}
      side={narrow}
      pager={
        <Turn
          returned={held.page.returned}
          more={held.page.more}
          next={held.page.next}
          back={trail.length === 0 ? null : () => setTrail((now) => now.slice(0, -1))}
          onNext={(to) => setTrail((now) => [...now, to])}
        />
      }
      foot={
        <>
          <Kept retention={held.retention} />
          <Gaps gaps={held.cannotAnswer} />
        </>
      }
    >
      <section>
        <SecLabel info="Times use your local zone. Newest activity appears first. Creating an export adds a row; viewing this page does not.">
          {narrowed ? "What matches" : "What happened here"}
        </SecLabel>

        <ListState
          state={paged.status === "reading" ? "reading" : "ready"}
          rows={events}
          cols={5}
          unit="recorded acts"
          empty={
            narrowed ? (
              <Empty title="No activity matches">
                <p>Clear one or both filters to see more.</p>
              </Empty>
            ) : cursor !== null ? (
              <Empty title="You reached the end">
                <p>Go back to see newer activity.</p>
              </Empty>
            ) : (
              // NOT "nothing has happened yet", WHICH IS THE DESIGN'S EMPTY
              // STATE AND IS A CLAIM THIS SCREEN CANNOT MAKE. Three sources are
              // unioned here and the feed is granted through two views under
              // row-level security, so an activation with no capture events, no
              // published configuration and no export produced is one reading of
              // an empty page and it is not the only one.
              <Empty title="No activity yet">
                <p>No registration, configuration publish or export is recorded for this activation.</p>
              </Empty>
            )
          }
        >
          <>
            <div className="tablewrap">
              <div className="tablescroll">
                <table className="activity-log">
                  <thead>
                    {/* Every column sorts, and the service does the sorting:
                        the keys are ACTIVITY_SORT_KEYS, and the cursor the
                        pager walks with belongs to the order it was read in. */}
                    <tr>
                      <SortHead of="when" {...heads}>
                        When
                      </SortHead>
                      <SortHead of="action" {...heads}>
                        What happened
                      </SortHead>
                      <SortHead of="entity" {...heads}>
                        Entity
                      </SortHead>
                      <SortHead of="actor" {...heads}>
                        Who acted
                      </SortHead>
                      <SortHead of="detail" {...heads}>
                        What it touched
                      </SortHead>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((line) => (
                      <Row key={line.activityId} line={line} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        </ListState>

        {/* THE SERVICE'S SENTENCE ABOUT ITS OWN ACTOR COLUMN, UNDER THAT COLUMN,
            WHICH IS WHERE THE SERVICE SAYS IT GOES. Its type comment makes the
            call: one sentence under a table beats the same clause repeated down
            it. It is also a different sentence for each audience, so this is
            drawn rather than written here. A client viewer is told what is
            withheld and that Nova holds it; Nova is told that a shared token
            names nobody. Neither is a paraphrase of the other and this console
            has no business choosing between them. */}
        <div className="panel panel-stack">
          <p>{held.attributionNote}</p>
        </div>
      </section>
    </DeskPage>
  );
}

// -------------------------------------------------------------------- the rows

/**
 * One recorded act.
 *
 * FIVE COLUMNS WHERE THE DESIGN HAS SIX. The sixth is the View button and
 * `cannot_answer` refuses the page behind it; the header has that argument.
 *
 * The source is under the action rather than in a column of its own, because it
 * is not a second thing that happened: it is which of 131's three records this
 * one was read out of, and the operator module's reason for carrying it at all
 * is that somebody looking at two rows for one export needs to tell them apart
 * without reading a file. Under the action is where somebody comparing two rows
 * is already looking.
 */
function Row({ line }: { readonly line: ActivityLine }): ReactNode {
  return (
    <tr>
      <td data-label="When" className="num">
        {when(line.occurredAt)}
      </td>
      <td data-label="What happened" className="mono">
        {line.action}
        <p className="t-note">{SOURCE[line.source] ?? line.source}</p>
      </td>
      <td data-label="Entity">
        <span className="mono">{line.entity}</span>
        {/* The reference under the kind, which is the design's second line. A
            row about a person carries the pseudonym that activation's own
            records use, and 131 refuses to put a name or an address here. Two
            of the three sources have rows with nothing to reference, and that
            absence is drawn as itself rather than as an empty cell. */}
        <p className="t-note mono activity-ref">
          {line.entityId ?? <span className="quiet">nothing referenced</span>}
        </p>
      </td>
      <td data-label="Who acted">
        <span className="activity-actor">{line.actor}</span>
        {/* How it was proved, which is the one of 131's four actor columns that
            is not already inside the phrase above on every row. On a capture row
            it is 018's channel, and that decides whether the event was signed at
            capture by a key in a hardware keystore or records having no
            signature and why, which the audit says a client viewer is entitled
            to see. */}
        {line.actorCredential === null ? null : (
          <p className="t-note mono">{line.actorCredential}</p>
        )}
        <Attribution of={line.attribution} />
      </td>
      <td data-label="What it touched">
        {line.detail ?? <span className="quiet">nothing recorded</span>}
      </td>
    </tr>
  );
}

/**
 * Which of the three states the reader is in about who acted.
 *
 * THE WHOLE POINT OF THIS COMPONENT IS THAT THE THREE ARMS LOOK DIFFERENT, and
 * the header has the argument at length. In short: a blank cell is the one
 * rendering of this that is wrong on every arm at once, and the specific harm
 * 131 names is a client reading the empty actor column of their own export row
 * as nobody having run it.
 *
 * A TAG RATHER THAN A SENTENCE ON EACH ARM, because the sentence is the
 * service's `attribution_note` under the table and it is a different sentence
 * for each audience. Three words in a cell that are visibly not each other, plus
 * one paragraph that says what all three mean, is the arrangement the service
 * asked for. Three paragraphs down a column would be the arrangement it argues
 * against, and this console writing its own would be a second opinion about who
 * this service can name.
 *
 * `recorded` WITH NOTHING IN IT IS A FOURTH APPEARANCE AND NOT A BUG. A shared
 * admin token is somebody having pressed a button where nobody can say who, and
 * 011's own column comment calls that the value a reader should act on. Drawn as
 * "no person" it would say a machine did it, and drawn as "withheld" it would
 * say Nova is holding the name back. It is neither: there is no name.
 */
function Attribution({ of }: { readonly of: ActivityAttribution }): ReactNode {
  // ONE WRAPPER ON EVERY ARM RATHER THAN A FRAGMENT ON SOME OF THEM. What the
  // three arms have in common is that they are the answer to one question, and
  // two of them are a single mark while `recorded` can be three lines. Without a
  // container, the arm that draws a bare identifier is indistinguishable from
  // the arm that drew nothing at all, both to a reader scanning the column and
  // to check.mjs comparing the two readings of one page.
  return (
    <div className="activity-attrib">
      <Arm of={of} />
    </div>
  );
}

/**
 * Which of the three it is, and nothing about where it sits.
 *
 * SPLIT OUT SO THE WRAPPER IS NOT OPTIONAL. Inlined, two arms would return a
 * bare tag and one would return a fragment, and the container above would be
 * something the third arm happened to need rather than the shape of the answer.
 * The switch is then the only thing in this function, which is the one place on
 * this screen where a missing arm has to be a compile error.
 */
function Arm({ of }: { readonly of: ActivityAttribution }): ReactNode {
  if (of.kind === "withheld") return <span className="tag tag-off">withheld</span>;
  if (of.kind === "none") return <span className="tag">no person</span>;

  const nothingToName = of.identityId === null && of.claimed === null;

  return (
    <>
      {nothingToName ? <span className="tag tag-warn">no name held</span> : null}
      {of.identityId === null ? null : <span className="mono activity-ref">{of.identityId}</span>}
      {of.claimed === null ? null : (
        // A NAME AND A WARNING BESIDE IT, BECAUSE THIS IS THE ARM THE SERVICE
        // SAYS NOTHING CHECKED. A publish records whatever the operator typed
        // into the request, and 107 renamed the column it comes out of to stop a
        // query treating it as a proved identity. Drawn like the uuid above it,
        // it would read as the same class of fact.
        <span>
          {of.claimed} <span className="tag tag-warn">not checked</span>
        </span>
      )}
      {of.sourceAddress === null ? null : <span className="mono activity-ref">{of.sourceAddress}</span>}
    </>
  );
}

// ----------------------------------------------------------------- the filters

/**
 * The two things the service will narrow this feed by.
 *
 * BOTH LISTS ARE THE SERVICE'S CONSTANTS AND NEITHER IS TYPED OUT HERE, which
 * is the decision behind the import at the top of this file. `ACTIVITY_ACTIONS`
 * is checked against the view's own `case` by xp/api/check.mjs, so an option in
 * this select is an action the feed can emit and can never be one the service
 * answers 400 to.
 *
 * SELECTS RATHER THAN THE DESIGN'S TEXT BOX FOR THE SECOND ONE. The design makes
 * entity free text over "people, p-8251, exports", which is three different
 * queries: a kind, a reference and a kind again. The service takes one of eight
 * words and holds no filter on `entity_id` at all, so a text box would refuse
 * most of what anybody typed into it, and a reference somebody pasted would come
 * back a 400 naming eight words that are not references.
 *
 * AND NO THIRD CONTROL, WHICH IS THE ABSENCE WORTH READING THE PARAGRAPH FOR.
 * The design's "Who did it" matches an email address, `cannot_answer` says there
 * is no address here for anybody to match, and the kind of thing that acted is
 * what could honestly be narrowed on instead. The read takes `action` and
 * `entity`, so there is no way to ask the service for it, and the alternative
 * was a control that narrowed the fifty rows in this browser while sitting
 * beside two that narrow the feed. That is the confusion `oneOf` refuses a
 * request to prevent one parameter over: a short answer and a wrong filter look
 * identical, and the reading a person takes is the reassuring one.
 */
function Narrow({
  action,
  entity,
  onAction,
  onEntity,
}: {
  readonly action: string | null;
  readonly entity: string | null;
  readonly onAction: (action: string | null) => void;
  readonly onEntity: (entity: string | null) => void;
}): ReactNode {
  return (
    <section>
      <SecLabel>Filter activity</SecLabel>
      <div className="panel panel-stack">
        <div className="filters">
          <label className="field activity-narrow">
            <span className="label">Action</span>
            <select
              className="input"
              value={action ?? ""}
              onChange={(event) => onAction(event.target.value === "" ? null : event.target.value)}
            >
              {/* The empty value is what the service reads as "everything", so
                  the control's do-nothing position and the request's absent
                  parameter are the same thing rather than two states that have
                  to be kept in step. */}
              <option value="">Everything</option>
              {ACTIVITY_ACTIONS.map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Entity</span>
            <select
              className="input"
              value={entity ?? ""}
              onChange={(event) => onEntity(event.target.value === "" ? null : event.target.value)}
            >
              <option value="">Everything</option>
              {ACTIVITY_ENTITIES.map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </select>
          </label>
          <div className="field">
            <span className="label" aria-hidden="true">
              &nbsp;
            </span>
            <button
              type="button"
              className="btn btn-quiet"
              disabled={action === null && entity === null}
              onClick={() => {
                onAction(null);
                onEntity(null);
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------- the pager

/**
 * The next page, and the way back to one already seen.
 *
 * NO PAGE COUNT, NO TOTAL AND NO "showing X of Y", WHICH IS NOT A PAGER WITH
 * PIECES MISSING. The shared `Pager` takes a total and divides by the page size,
 * and this feed has no total on purpose: it is three sources unioned, one of
 * which is every scan of the show, so counting it is a second pass over the
 * whole union on every page to produce a figure that is stale before it is
 * drawn. The service's own type says both that and why an offset is worse than
 * absent. What arrives instead is `more`, which costs one row.
 *
 * SO THE LINE IN THE MIDDLE IS A COUNT OF THIS PAGE AND NOT A POSITION IN A
 * LIST. It is the service's `returned`, which is also the number filed as this
 * disclosure's `row_count`, so it is the one figure here a reader might be asked
 * about later.
 *
 * NEXT NEEDS BOTH `more` AND A CURSOR. They come from the same statement and
 * agree, and requiring both anyway is about which way the disagreement would
 * fail: a button offered on `more` alone with no cursor to send would re-read
 * the page the reader is on, which looks like a feed that has stopped rather
 * than like a fault.
 */
function Turn({
  returned,
  more,
  next,
  back,
  onNext,
}: {
  readonly returned: number;
  readonly more: boolean;
  readonly next: string | null;
  /** Null on the first page, which is the only place there is nowhere back to. */
  readonly back: (() => void) | null;
  readonly onNext: (cursor: string) => void;
}): ReactNode {
  // The shared pager's markup, .pg-in included: the pager is a container and
  // lays out its inside by its own width (furniture.css), the same two ways
  // as the offset one, with no Per page and no "n of m" in them.
  return (
    <div className="pager">
      <div className="pg-in">
        <p className="pg-n">
          {returned} recorded {returned === 1 ? "act" : "acts"} on this page
        </p>
        <div className="pg-b">
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            disabled={back === null}
            onClick={() => back?.()}
          >
            Back
          </button>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            disabled={!more || next === null}
            onClick={() => {
              if (next !== null) onNext(next);
            }}
          >
            Older
          </button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------- the two notes

/**
 * When this goes, which for this feed is the question with the surprising
 * answer.
 *
 * THE SENTENCE IS THE SERVICE'S AND THE TWO FACTS ARE THE WORKING, which is
 * Unverified.tsx's arrangement, and here the sentence is doing more work than it
 * does there. There, it says when rows are removed, because something removes
 * them. Here it says that nothing removes any of this, that the agreed period
 * is what was agreed rather than what is enforced, and that deleting on it is
 * unbuilt. That is the specific claim the design's footer gets wrong, so
 * repeating the design would be writing down the opposite of what happens.
 *
 * NOT AMBER WHEN NO PERIOD IS AGREED, WHICH IS WHERE THIS PARTS COMPANY WITH
 * Unverified.tsx AND THE REASON IS THE SAME ONE. There, a missing retention
 * period is actionable: 130's purge reads that column every time it runs, so
 * recording the agreement later reaches the addresses already collected. Here
 * nothing reads it, so a missing period changes nothing that a recorded one
 * would have changed, and an amber notice would be an alarm about a value with
 * no consequence attached. The quiet box says the true thing instead, which is
 * that neither state deletes anything.
 */
function Kept({ retention }: { readonly retention: ActivityRetention }): ReactNode {
  return (
    <section>
      <SecLabel>Retention</SecLabel>

      <DefList
        rows={[
          [
            "Show sealed",
            retention.sealedAt === null ? (
              <span className="quiet">not sealed: this activation&rsquo;s record is still open</span>
            ) : (
              when(retention.sealedAt)
            ),
          ],
          [
            "Agreed retention",
            retention.retentionMonths === null ? (
              <span className="tag tag-warn">not agreed</span>
            ) : (
              <>
                {retention.retentionMonths} {retention.retentionMonths === 1 ? "month" : "months"}
              </>
            ),
          ],
        ]}
      />

      <NoticeBox kind="quiet" title="This activity follows a different retention rule">
        <p>{retention.note}</p>
      </NoticeBox>
    </section>
  );
}

/**
 * The service's list of what it cannot answer, stated rather than drawn.
 *
 * `what` IS THE DESIGN'S OWN PHRASE FOR THE THING, deliberately, so that
 * somebody holding the design pack next to this screen can match them up one for
 * one instead of wondering which column went missing. Both strings come off the
 * response and neither is edited here, which is People's rule and the reason
 * this component is shaped like its `Gaps`.
 *
 * SEVEN OF THEM, WHICH IS WHY THE LEDE DOES NOT COUNT THEM. People's says four
 * because four is what that read holds frozen; a number written into this screen
 * would be a number to maintain against a list this screen does not own.
 */
function Gaps({ gaps }: { readonly gaps: readonly ActivityGap[] }): ReactNode {
  return (
    <section>
      <SecLabel info="These details are not collected, so they cannot be searched or opened here.">
        Not available
      </SecLabel>
      <div className="panel panel-stack">
        {gaps.length === 0 ? (
          <p className="help help-tight">Details are unavailable for this response.</p>
        ) : (
          <ul className="reasons">
            {gaps.map((gap) => (
              <li key={gap.what}>
                <b className="activity-gap">{gap.what}.</b> {gap.why}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- small things

/**
 * Which of 131's three records a row was read out of, in the reader's words.
 *
 * A LABEL AND A FALLBACK TO THE SERVICE'S WORD, which is People's `LANE` rule
 * exactly: a word this console has no label for is still a fact, and mapping it
 * to the nearest known one is how a fourth source gets reported as a third.
 * `report_access` is the one worth the translation, because it is the arm whose
 * own name is about the audit table rather than about what the row records, and
 * 131 confines it to the export reads.
 */
const SOURCE: Readonly<Record<string, string>> = {
  spine: "capture event",
  config_bundle: "published configuration",
  report_access: "record of a file produced",
};

/**
 * A timestamp in the reader's own zone, or the fact that none was sent.
 *
 * NOT THE VENUE'S CLOCK, WHICH IS WHAT THE DESIGN'S OWN NOTE PROMISES. The
 * service sends UTC and nothing on this response carries a hall's zone, so a
 * line reading "Europe/London" under this table would be a claim about where the
 * reader is sitting. People.tsx and Unverified.tsx both draw a moment this way
 * and the help text above says which zone it is.
 *
 * An unparseable string is drawn as what the service said rather than as the
 * word for nothing, which is People's rule and Ride.tsx's before it: the service
 * did say something, and whoever has to work out why wants to see it.
 */
function when(value: string | null): ReactNode {
  if (value === null || value === "") return <span className="quiet">not recorded</span>;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
